/**
 * Product data fetching and transformation utilities
 * Retrieves and formats product pricing and variant information from Storefront API
 */

import { globalState } from '@/components/helpers/utils'
import { QUERY_VARIANT_PRICES } from './queries'
import { storefront } from 'libs/storefront/client'

const VARIANT_GID_PREFIX = 'gid://shopify/ProductVariant/'

/** `nodes(ids:)` accepts a maximum of 250 ids per request. */
const MAX_IDS_PER_QUERY = 250

/**
 * Market price for a single variant, in the presentment currency of the
 * requested country. `compareAtPrice` is null when the variant isn't on sale in
 * that market — callers must clear any base-currency compare-at they hold rather
 * than mixing currencies.
 */
export interface VariantPrice {
  price: string;
  compareAtPrice: string | null;
}

/**
 * Normalise a variant id to its numeric form, accepting either a raw id or a GID
 */
const toVariantId = (id: string | number | undefined | null): string => {
  if (id === undefined || id === null) return ''
  return `${id}`.split('/').pop() ?? ''
}

const toVariantGid = (id: string): string => `${VARIANT_GID_PREFIX}${id}`

const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = []
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size))
  }
  return chunks
}

/**
 * Fetches variant prices in the current market's currency
 * @param variantIds - Variant ids (raw or GID) to price
 * @returns Promise of a variant id → price map, or null if nothing could be resolved
 */
async function getVariantPrices(variantIds: Array<string | number>): Promise<Map<string, VariantPrice> | null> {
  const ids = [...new Set((variantIds ?? []).map(toVariantId).filter(Boolean))]

  if (!ids.length) {
    console.error('No variant ids supplied for price localisation')
    return null
  }

  if (!globalState?.localization?.country) {
    console.warn('Country code not found in global state')
    return null
  }

  try {
    const country = globalState.localization.country

    const responses = await Promise.all(
      chunk(ids, MAX_IDS_PER_QUERY).map((batch) => storefront.query({
        query: QUERY_VARIANT_PRICES,
        variables: {
          country,
          ids: batch.map(toVariantGid)
        }
      }))
    )

    const prices = new Map<string, VariantPrice>()

    for (const response of responses) {
      if (response.errors) {
        throw new Error(JSON.stringify(response.errors))
      }

      for (const node of response.data?.nodes ?? []) {
        const id = toVariantId(node?.id)
        if (!id || !node?.price?.amount) continue

        prices.set(id, {
          price: node.price.amount,
          compareAtPrice: node.compareAtPrice?.amount ?? null
        })
      }
    }

    if (!prices.size) {
      console.warn('No variant prices resolved for the current market — check that the Athos feed exposes variant ids')
      return null
    }

    return prices
  } catch (error) {
    console.error('Error fetching variant prices:', error)
    return null
  }
}

export { getVariantPrices, toVariantId }
